// The printable Takeoff Report — the model behind it (pure; the page is TakeoffReport.jsx).
//
// Built on the SAME three tables the CSV export produces (utils/takeoffExport.js: by condition / by drawing /
// by room), which read every quantity through computeConditionQuantities — the function behind the canvas labels,
// the dock and Σ Totals — so the report, the CSV and the canvas reconcile by construction. This module adds what a
// printed report needs: which columns to show, grouping with subtotals, a plain statement of any rounding that was
// applied, and the file name. Takeoff carries NO waste (owner decision): there is no waste column anywhere.
//
// The display units (imperial / metric) are applied by the builders; this module never converts anything itself.

import { byConditionReport, byDrawingReport, byRoomReport, toCsv, csvFileName } from "./takeoffExport";
import { fmtArea } from "./units";

export const REPORT_TABS = Object.freeze([
  { id: "condition", label: "By condition", build: byConditionReport },
  { id: "drawing", label: "By drawing", build: byDrawingReport },
  { id: "room", label: "By room", build: byRoomReport },
]);

// How each table can be grouped (first = the default). A grouped table shows a header per group and subtotals.
export const GROUPINGS = Object.freeze({
  condition: [{ id: "none", label: "No grouping" }, { id: "costCode", label: "By cost code" }],
  drawing: [{ id: "drawing", label: "By drawing" }, { id: "none", label: "No grouping" }],
  room: [{ id: "room", label: "By room" }, { id: "none", label: "No grouping" }],
});

// Columns that are never printed (the report's own header says it) or hidden until asked for.
const ALWAYS_HIDDEN = new Set(["revision", "csi", "costCode"]);
export const DEFAULT_HIDDEN = Object.freeze({
  condition: ["qty2", "uom2", "qty3", "uom3"],
  drawing: ["qty2", "uom2", "qty3", "uom3", "instances"],
  room: ["qty2", "uom2", "qty3", "uom3", "instances"],
});

const tabDef = (tab) => REPORT_TABS.find(t => t.id === tab) || REPORT_TABS[0];

/** The columns a table offers (after the always-hidden ones), each with whether it is shown. */
export function columnChoices(tab, data, hidden = DEFAULT_HIDDEN[tab] || []) {
  const { columns } = tabDef(tab).build({ rooms: [], conditions: [], sheets: [], ...data });
  return columns.filter(c => !ALWAYS_HIDDEN.has(c.key)).map(c => ({ key: c.key, label: c.label, shown: !hidden.includes(c.key) }));
}

const num = (v) => (typeof v === "number" && Number.isFinite(v) ? v : null);

/** Sum of the first reading per unit across rows — what a group's subtotal line says. */
export function subtotalsOf(rows) {
  const by = new Map();
  for (const r of rows) {
    const q = num(r.qty);
    if (q == null || !r.uom) continue;
    by.set(r.uom, (by.get(r.uom) || 0) + q);
  }
  return [...by.entries()].map(([uom, qty]) => ({ uom, qty: Math.round(qty * 1000) / 1000 }));
}

function groupBy(rows, keyOf, labelOf, subOf) {
  const order = [], map = new Map();
  for (const r of rows) {
    const k = keyOf(r);
    if (!map.has(k)) { map.set(k, { key: k, label: labelOf(r), sub: subOf ? subOf(r) : "", rows: [] }); order.push(k); }
    map.get(k).rows.push(r);
  }
  return order.map(k => ({ ...map.get(k), subtotals: subtotalsOf(map.get(k).rows) }));
}

const join = (...p) => p.filter(x => x != null && x !== "").join(" — ");

/**
 * One table ready to render.
 *   data: { rooms, conditions, sheets, units, revision }
 *   -> { id, columns (visible), groups: [{ key, label, sub, rows, subtotals }], rowCount, grouped }
 * `group: 'none'` gives one unlabelled group.
 */
export function buildReportTable(tab, data, { group, hidden } = {}) {
  const def = tabDef(tab);
  const mode = group ?? GROUPINGS[def.id][0].id;
  const hide = new Set([...(hidden ?? DEFAULT_HIDDEN[def.id] ?? []), ...ALWAYS_HIDDEN]);
  const { columns, rows } = def.build(data);
  let groups;
  if (mode === "costCode" && def.id === "condition") {
    groups = groupBy(rows, r => r.csi || "", r => (r.csi ? join(r.csi, r.costCode) : "Uncoded"));
    groups.sort((a, b) => (a.key === "" ? 1 : b.key === "" ? -1 : a.key.localeCompare(b.key, undefined, { numeric: true })));
  } else if (mode === "drawing" && def.id === "drawing") {
    groups = groupBy(rows, r => `${r.drawingNumber}|${r.drawing}`, r => join(r.drawingNumber, r.drawing) || "No drawing");
  } else if (mode === "room" && def.id === "room") {
    groups = groupBy(rows, r => `${r.drawingNumber}|${r.roomNumber}|${r.room}`, r => join(r.roomNumber, r.room) || "No room",
      r => [r.drawingNumber || r.drawing, [r.lengthFt, r.widthFt, r.heightFt].some(v => v != null) ? `${r.lengthFt ?? "—"} × ${r.widthFt ?? "—"} × ${r.heightFt ?? "—"}` : "", r.floorSf != null ? `floor ${r.floorSf}` : ""].filter(Boolean).join(" · "));
  } else {
    groups = [{ key: "all", label: "", sub: "", rows, subtotals: subtotalsOf(rows) }];
  }
  return {
    id: def.id, grouped: groups.length > 0 && groups[0].label !== "", rowCount: rows.length,
    columns: columns.filter(c => !hide.has(c.key)), groups,
  };
}

const fmtIn = (inches, units) => (units === "metric" ? `${Math.round(inches * 25.4)} mm` : `${inches} in`);

/**
 * Plain statements of any rounding in force: which conditions are rounded and to what, and that hand-entered
 * quantities are not. [] when nothing is rounded.
 */
export function roundingNotes(conditions, units = "imperial") {
  const seen = new Map();
  let overrides = 0;
  for (const c of conditions || []) {
    if (c.MeasureRole === "deduct") continue;
    if (c.QuantityOverride != null) overrides += 1;
    const key = `${c.Kind}:${c.ItemID ?? c.AssemblyID ?? c.TakeoffConditionLibraryID}`;
    if (seen.has(key)) continue;
    const label = c.Code || c.Name || "Condition";
    if (c.Style === "area" && c.RoundAreaToSf > 0) seen.set(key, `${label} — each measured area is rounded to the nearest ${units === "metric" ? fmtArea(c.RoundAreaToSf, units, 2) : `${c.RoundAreaToSf} SF`}.`);
    else if (c.Style === "linear" && c.RoundToIn > 0) seen.set(key, `${label} — each measured run is rounded to the nearest ${fmtIn(c.RoundToIn, units)}.`);
  }
  const notes = [...seen.values()];
  if (notes.length && overrides) notes.push(`${overrides} quantity${overrides === 1 ? " was" : "s were"} entered by hand and ${overrides === 1 ? "is" : "are"} not rounded.`);
  return notes;
}

/** The visible columns, flat — what the CSV of the report contains (same rows as the table, group headers not repeated). */
export function reportCsv(tab, data, { hidden, project = "", revision = "", date = new Date() } = {}) {
  const t = buildReportTable(tab, data, { group: "none", hidden });
  const def = tabDef(tab);
  const stamp = new Date(date).toISOString().slice(0, 10);
  const rows = t.groups.flatMap(g => g.rows);
  return { filename: csvFileName([project, revision, "takeoff-report", def.id, stamp]), csv: toCsv(t.columns, rows), rowCount: rows.length };
}

/** The line under the title: units, drawings and what the numbers are. */
export function reportSubtitle({ units = "imperial", drawingCount = 0, conditionCount = 0 } = {}) {
  return [
    units === "metric" ? "Metric (m, m²)" : "Feet and inches (LF, SF)",
    `${drawingCount} drawing${drawingCount === 1 ? "" : "s"}`,
    `${conditionCount} condition${conditionCount === 1 ? "" : "s"}`,
  ].join(" · ");
}

export const REPORT_DISCLAIMER = "Quantities are measured from the drawings as drawn, and rounded as noted. This is a draft for estimator review — verify against the drawings before any bid or customer use.";

